import { Pagination, Response } from 'shared/models/general'

export interface RealtySearchRequest extends Pagination {
  page: number
  page_size: number
  sort?: string | null
  start_date?: string
  end_date?: string
  city_id?: number | number[]
  visitors_count?: number
  children_ages?: number[]
  realty_type_id?: string
  price_min?: number
  price_max?: number
  order?: string
}

export interface RealtyType {
  id: number
  name: string
  icon?: string
  count?: number
  has_children?: boolean
}

export interface RealtyCity {
  id: number
  city_name: string
  region: string | null
  country_code: string
  country_name: string
}

export interface RealtyCountry {
  code_name: string
  country_name: string
}

export interface RealtyOwner {
  id: number
  email: string
  first_name: string | null
  last_name: string | null
  middle_name: string | null
  phone: string | null
}

export interface RealtyTimes {
  arrival_time: string
  departure_time: string
}

export interface RealtyUnit {
  id: string
  realty_object_id: string
  number: number
  name: string
  description: string
  bookit_until_date: string
}

export interface RealtyImage {
  id: string
  order: number
  image_url: string
}

export interface RealtyInfo {
  id: string
  title: string
  subtitle: string
  description: string
  icon: string
  isShowReturnPolicy?: boolean
}

export interface DynamicFieldValue {
  items: DynamicFieldValueItem[]
  count: number
}

export interface DynamicFieldValueItem {
  id: number
  name: string
  slug: string
  icon: any
  order: number
  fields: DynamicField[]
}

export interface DynamicField {
  id: number
  name: string
  field_type: 'BOOLEAN' | 'TEXT' | 'NUMBER'
  icon?: string
  is_favor: boolean
  is_active: boolean
  value?: string
}

export enum RealtySource {
  INTERNAL = 'INTERNAL',
  EXELY = 'EXELY',
  STAYBOOK = 'STAYBOOK',
  WEBVIEW = 'WEBVIEW',
  RATEHAWK = 'RATEHAWK',
}

export interface CancellationPolicy {
  free_cancellation_possible: boolean
  free_cancellation_deadline_local: string
  free_cancellation_deadline_utc: string
  penalty_amount: string
  estimated_discounted_penalty_amount: null
  currency_code: string
  timezone: string
}

export interface RealtyObject {
  id: string
  type: RealtyType
  parent_id: string
  longitude: number
  latitude: number
  city: RealtyCity
  country: RealtyCountry
  address: string
  price: string
  limit_units: number
  title: string
  description: string
  stars: number
  owner: RealtyOwner
  free_cancellation_minutes: number
  images: RealtyImage[]
  infos: RealtyInfo[]
  times: RealtyTimes
  nights: number | null
  total_price: string | null
  dynamic_fields: DynamicFieldValue
  popular_dynamic_fields: DynamicField[]
  mbonus_available: boolean
  source: RealtySource
  cancellation_policy?: CancellationPolicy | null
  exely_penalty_starts_at?: string | null
  ratehawk_book_hash: string | null
  timezone: string
  start_date?: string
  end_date?: string
  visitors_count?: number
}

export interface RealtySearchResponse
  extends Response<RealtyObject[]>, Required<Pagination> {
  max_price: number
  min_price: number
}

export interface RealtyTypesRequest {
  has_object?: boolean
  is_favor?: boolean
}

export interface RealtyPriceRequest {
  realty_object_id: string
  nights: number
  visitors_count: number
}

export interface RealtyPriceResponse {
  total_price: string
  prepayment_price: string
}

export interface RealtyPriceWithAvailabilityRequest {
  realty_object_id: string
  nights: number
  visitors_count: number
  children_ages?: number[]
  start_date: string
  ratehawk_book_hash?: string
}

export interface RealtyPriceWithAvailabilityResponse {
  total_price: string
  prepayment_price: string
  units_count: number
  mbonus_available: boolean
  mbonus_amount: number
  ratehawk_book_hash?: string | null
}

export interface RealtyCapacityResponse {
  max_guests: number
  available_rooms: number
  max_guests_per_room: number
}

export interface RealtyCapacityRequest {
  start_date: string
  end_date: string
}

export interface AvailableDatesRequest {
  visitors_count: number
  children_ages?: number[]
}

export interface AvailableDatesResponse {
  available_dates: string[]
}
